import * as React from "react";
import { Loader2, ArrowRight, Package, Boxes, Plus, Check } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { Modal } from "@/components/Modal.js";
import { useSpaces } from "@/hooks/useSpaces.js";
import { cn } from "@/lib/utils.js";

/**
 * ReorganizeFromToModal — the "from → to containers" bulk reorganize flow (F-e).
 *
 * Expresses reorganize as movement at the container level: pick SOURCE
 * containers, seed the item set from their contents, then pick one or more
 * TARGET containers (including additional/empty ones already defined under the
 * room). It compiles down to a single placement trial with per-item targets
 * (F-d): items default to the primary target, and any item can be re-pointed at
 * another chosen target. Keep/put-back then works over the whole trial.
 *
 * Containers ARE child spaces (Decision R1): a container is a space with
 * type:"container" and a parentId pointing to its room — so "from"/"to" map to
 * existing space IDs with no new entity.
 */

interface SpaceItem { id: string; name: string; spaceId: string | null; spaceName: string | null }

export function ReorganizeFromToModal({
  open,
  onClose,
  goalId,
  goalSpaceId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  goalId: string;
  /** The goal's activity space (the room or container being reorganized). */
  goalSpaceId: string | null;
  /** Called after the trial is created so the parent can refetch. */
  onCreated: () => Promise<void> | void;
}) {
  const toast = useToast();
  const { spaces } = useSpaces();
  const [sourceIds, setSourceIds] = React.useState<string[]>([]);
  const [targetIds, setTargetIds] = React.useState<string[]>([]);
  const [items, setItems] = React.useState<SpaceItem[]>([]);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [itemTarget, setItemTarget] = React.useState<Record<string, string>>({});
  const [creating, setCreating] = React.useState(false);

  // The room to scope containers to: the goal's space if it's a room, else the
  // parent of the goal's container. Falls back to "all containers" when unknown.
  const roomId = React.useMemo(() => {
    const gs = spaces.find((s) => s.id === goalSpaceId);
    if (!gs) return null;
    return gs.type === "container" ? gs.parentId : gs.id;
  }, [spaces, goalSpaceId]);

  // Containers in play: those under the room (or all containers if no room ctx).
  const containers = React.useMemo(
    () => spaces.filter((s) => s.type === "container" && (roomId ? s.parentId === roomId : true)),
    [spaces, roomId]
  );

  // Reset when opened.
  React.useEffect(() => {
    if (!open) return;
    setSourceIds([]);
    setTargetIds([]);
    setItems([]);
    setItemTarget({});
  }, [open]);

  // Seed the item set from the selected source containers' contents (§3.3).
  React.useEffect(() => {
    if (!open || sourceIds.length === 0) { setItems([]); return; }
    let cancelled = false;
    setItemsLoading(true);
    apiFetch<SpaceItem[]>(`/api/items?spaceId=${sourceIds.join(",")}&limit=200`)
      .then((data) => { if (!cancelled) setItems(data); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setItemsLoading(false); });
    return () => { cancelled = true; };
  }, [open, sourceIds]);

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const primaryTarget = targetIds[0] ?? "";
  const spaceName = (id: string) => spaces.find((s) => s.id === id)?.name ?? "space";

  // Items that would actually move (their current space isn't their assigned target).
  const movingItems = React.useMemo(
    () => items.filter((it) => (itemTarget[it.id] ?? primaryTarget) !== it.spaceId),
    [items, itemTarget, primaryTarget]
  );

  const canCreate = sourceIds.length > 0 && targetIds.length > 0 && movingItems.length > 0 && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const itemIds = movingItems.map((it) => it.id);
      // Per-item targets for anything not going to the primary target.
      const itemTargets: Record<string, string> = {};
      for (const it of movingItems) {
        const dest = itemTarget[it.id] ?? primaryTarget;
        if (dest !== primaryTarget) itemTargets[it.id] = dest;
      }
      await apiFetch(`/api/organizing-goals/${goalId}/trials`, {
        method: "POST",
        body: JSON.stringify({
          targetSpaceId: primaryTarget,
          itemIds,
          ...(Object.keys(itemTargets).length > 0 ? { itemTargets } : {}),
        }),
      });
      toast.show("Reorganize trial created — move the items when you're ready.", "success");
      await onCreated();
      onClose();
    } catch (err: any) {
      toast.show(err.message || "Couldn't create the reorganize trial", "error");
    }
    setCreating(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="Reorganize containers">
      <div className="space-y-4">
        {containers.length < 2 ? (
          <div className="text-center py-6">
            <Boxes className="w-8 h-8 text-faint mx-auto mb-2" />
            <p className="text-sm text-body mb-1">Reorganize needs at least two containers.</p>
            <p className="text-[0.8125rem] text-faint">Add containers (drawers, shelves, bins) to this room, then come back.</p>
          </div>
        ) : (
          <>
            {/* From → To picker */}
            <div className="flex items-stretch gap-2">
              <div className="flex-1">
                <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1.5 block">From</label>
                <div className="space-y-1">
                  {containers.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSourceIds((s) => toggle(s, c.id))}
                      className={cn(
                        "w-full text-left px-2.5 py-1.5 rounded-md text-[0.8125rem] border transition-colors",
                        sourceIds.includes(c.id)
                          ? "bg-amethyst-soft border-primary text-amethyst-ink font-medium"
                          : "bg-card border-clay-border text-body hover:border-primary/40"
                      )}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center pt-6">
                <ArrowRight className="w-4 h-4 text-faint" />
              </div>
              <div className="flex-1">
                <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1.5 block">To</label>
                <div className="space-y-1">
                  {containers.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setTargetIds((s) => toggle(s, c.id))}
                      className={cn(
                        "w-full text-left px-2.5 py-1.5 rounded-md text-[0.8125rem] border transition-colors flex items-center gap-1",
                        targetIds.includes(c.id)
                          ? "bg-amethyst-soft border-primary text-amethyst-ink font-medium"
                          : "bg-card border-clay-border text-body hover:border-primary/40"
                      )}
                    >
                      {targetIds.includes(c.id) ? <Check className="w-3 h-3 shrink-0" /> : <Plus className="w-3 h-3 shrink-0 text-faint" />}
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Seeded item set from source containers */}
            {sourceIds.length > 0 && (
              <div>
                <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1.5 block">
                  Items to move ({movingItems.length})
                </label>
                {itemsLoading ? (
                  <div className="flex items-center gap-2 py-3 text-[0.8125rem] text-faint">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading items…
                  </div>
                ) : items.length === 0 ? (
                  <p className="text-[0.8125rem] text-faint py-2">No items in the selected container(s).</p>
                ) : targetIds.length === 0 ? (
                  <p className="text-[0.8125rem] text-faint py-2">Pick a target container to move these into.</p>
                ) : (
                  <div className="max-h-52 overflow-y-auto space-y-1">
                    {items.map((it) => {
                      const dest = itemTarget[it.id] ?? primaryTarget;
                      const staying = dest === it.spaceId;
                      return (
                        <div key={it.id} className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-amethyst-soft/50 transition-colors">
                          <Package className="w-3.5 h-3.5 text-faint shrink-0" />
                          <span className="text-[0.8125rem] text-body flex-1 min-w-0 truncate">{it.name}</span>
                          {targetIds.length > 1 ? (
                            <select
                              value={dest}
                              onChange={(e) => setItemTarget((prev) => ({ ...prev, [it.id]: e.target.value }))}
                              className="text-[0.8125rem] px-1.5 py-1 border border-clay-border rounded-sm bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary max-w-[48%]"
                            >
                              {targetIds.map((tid) => (
                                <option key={tid} value={tid}>{spaceName(tid)}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={cn("text-[0.8125rem]", staying ? "text-faint" : "text-sage-deep")}>
                              → {spaceName(dest)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {items.length > 0 && movingItems.length < items.length && (
                  <p className="text-[0.8125rem] text-faint mt-1">
                    {items.length - movingItems.length} item(s) already in their target — they won't be moved.
                  </p>
                )}
              </div>
            )}

            <button
              onClick={handleCreate}
              disabled={!canCreate}
              className="w-full py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {creating ? "Creating…" : `Try moving ${movingItems.length || ""} item${movingItems.length === 1 ? "" : "s"}`.trim()}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
